import React, { type JSX } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { usePermissions } from "../context/PermissionsContext";
import type { RoleKey } from "../../config/permissions";

interface RequireAuthProps {
  children: JSX.Element;
  /**
   * Minimum role required to view this route.  Omit or pass null for
   * "any authenticated user".  Hierarchy is honoured by usePermissions.can().
   */
  role?: RoleKey | null;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children, role = null }) => {
  const { authenticated, loading, can } = usePermissions();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (role && !can(role)) {
    return <Navigate to="/dashboard" state={{ forbidden: true }} replace />;
  }

  return children;
};

export default RequireAuth;